<template>
  <div>
    <div class="bg-gray-800 text-white text-center py-2 px-4">
      <h1 class="text-lg">{{ videoDetails.mailing_title }}</h1>
    </div>
    <div class="inset-0 flex justify-center sm:px-8">
      <div class="flex flex-col w-full max-w-7xl lg:px-8 py-4 space-y-6">
        <div
          class="flex flex-col w-full bg-white p-4 rounded-lg space-y-4 shadow-md"
        >
          <div class="flex flex-row justify-between items-center space-x-2">
            <div
              class="bg-green-600 text-white px-2 py-1 rounded hidden sm:block uppercase font-medium"
              v-if="videoDetails.sale !== null"
            >
              Purchased
            </div>
            <div
              class="bg-blue-700 text-white px-2 py-1 rounded hidden sm:block"
            >
              ${{ videoDetails.price }}
            </div>
            <h1 class="font-medium flex-1 text-lg">{{ videoDetails.title }}</h1>
            <span
              class="float-none hidden sm:block"
              :title="videoDetails.created_at.date | displayLocalDateTime"
              v-tippy
              >{{ videoDetails.created_at.date | timeAgo }}</span
            >
          </div>

          <iframe
            class="w-full aspect-video rounded-lg shadow-md bg-black"
            :src="'https://www.youtube.com/embed/' + videoDetails.youtube_id"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen=""
          ></iframe>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div
              class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
            >
              <span class="text-xl md:text-2xl font-bold"
                >${{ videoDetails.price }}</span
              >
              <span class="mt-1 uppercase text-gray-400 font-medium text-sm"
                >price</span
              >
            </div>
            <div
              class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
            >
              <span class="text-xl md:text-2xl font-bold">{{
                videoDetails.credit
              }}</span>
              <span
                class="mt-1 uppercase text-gray-400 font-medium text-sm"
                v-if="videoDetails.credit > 1 || videoDetails.credit == 0"
                >credits</span
              >
            </div>
            <div
              class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
            >
              <span class="text-xl md:text-2xl font-bold">
                <StarRating id="vp" :rating="videoDetails.rating" />
              </span>
              <span class="mt-1 uppercase text-gray-400 font-medium text-sm"
                >rating</span
              >
            </div>
            <div
              class="p-6 border-2 rounded-lg flex flex-col items-center content-center"
            >
              <span class="text-xl md:text-2xl font-bold">
                {{
                  videoDetails.created_at.date | displayLocalDateTime("M/D/YY")
                }}
              </span>
              <span class="mt-1 uppercase text-gray-400 font-medium text-sm"
                >posted date</span
              >
            </div>
          </div>

          <div class="flex flex-col">
            <span class="uppercase text-gray-400 font-semibold text-xs"
              >Credit</span
            >
            <p>
              <span v-if="videoDetails.stringer !== 'Live Storms Media'"
                >{{ videoDetails.stringer }} / LSM</span
              >
              <strong
                v-if="videoDetails.stringer == 'Live Storms Media'"
                style="color: red;"
                >Check Description</strong
              >
            </p>
          </div>

          <div class="flex flex-col">
            <span class="uppercase text-gray-400 font-semibold text-xs"
              >Narrative</span
            >
            <p>
              {{ videoDetails.description }}
            </p>
          </div>

          <div class="flex flex-col">
            <span class="uppercase text-gray-400 font-semibold text-xs"
              >Locations</span
            >
            <div>
              <span
                class="rounded px-2 py-1 bg-green-500 text-white text-xs mr-2 inline-block"
                v-for="(location, index) in videoDetails.locations"
                v-bind:key="index"
                >{{ location }}</span
              >
              <span v-if="videoDetails.locations.length == 0"
                >Check video title.</span
              >
            </div>
          </div>

          <div
            class="grid gap-4 pt-6"
            :class="{
              'grid-cols-1 md:grid-cols-2':
                videoDetails.networkCreditAgreement !== null,
              'grid-cols-1': videoDetails.networkCreditAgreement == null
            }"
            v-if="videoDetails.sale == null"
          >
            <div
              class="col-span-1 md:col-span-2 text-red-600"
              v-if="
                videoDetails.networkCreditAgreement !== null &&
                  (videoDetails.videoExemptFromCreditAgreement ||
                    videoDetails.userExemptFromCreditAgreement)
              "
            >
              This video is unable to be purchased using credits due to
              restrictions.
            </div>
            <div
              class="col-span-1 md:col-span-2 text-red-600"
              v-if="
                videoDetails.networkCreditAgreement !== null &&
                  videoDetails.networkCreditAgreement.creditUsageDataStatistics
                    .creditsRemaining -
                    videoDetails.credit <
                    0
              "
            >
              This video is unable to be purchased using credits. The current
              network agreement does not have enough free credits to cover this
              transaction. You can however pay the listed price until a new
              agreement is established.
            </div>
            <button
              class="w-full px-2 py-2 bg-blue-600 text-white rounded-lg focus:outline-none active:bg-blue-500 transition-all disabled:cursor-not-allowed disabled:opacity-50"
              v-if="videoDetails.networkCreditAgreement !== null"
              v-on:click="purchaseUsingCredits()"
              :disabled="
                videoDetails.networkCreditAgreement.creditUsageDataStatistics
                  .creditsRemaining -
                  videoDetails.credit <
                  0 ||
                  videoDetails.videoExemptFromCreditAgreement ||
                  videoDetails.userExemptFromCreditAgreement
              "
            >
              Purchase Video ({{ videoDetails.credit }} Credits)
            </button>

            <button
              class="w-full px-2 py-2 bg-blue-600 text-white rounded-lg focus:outline-none active:bg-blue-500 transition-all"
              v-on:click="purchaseAndDownload()"
            >
              Purchase Video
              <span v-if="videoDetails.networkCreditAgreement !== null"
                >(Post Billing)</span
              >
            </button>
          </div>

          <button
            class="w-full px-2 py-2 bg-blue-600 text-white rounded-lg focus:outline-none active:bg-blue-500 transition-all"
            v-on:click="redownloadVideo()"
            v-if="videoDetails.sale !== null"
          >
            Download Video
          </button>
        </div>

        <div class="w-full bg-white p-4 rounded-lg space-y-4 shadow-md">
          <h2 class="font-medium">Additional Videos in Mailing</h2>
          <div class="flex overflow-x-auto space-x-4">
            <section
              class="flex-shrink-0 bg-gray-50"
              v-for="video in videoDetails.videos"
              v-bind:key="video.id"
              v-if="video.id !== videoDetails.id"
              v-on:click="goToMailing(video)"
            >
              <div class="h-40 aspect-video">
                <div class="stretch relative rounded-md overflow-hidden group">
                  <div
                    class="absolute w-full h-full z-20 bg-gray-600 bg-opacity-0 flex flex-col content-center items-center justify-center text-white text-center group-hover:bg-opacity-75 transition-all cursor-pointer"
                  >
                    <p
                      class="pt-2 px-2 lg:px-10 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      {{ video.title }}
                    </p>
                  </div>
                  <img
                    class="filter-grayscale z-0 w-full blur "
                    :src="
                      'https://img.youtube.com/vi/' +
                        video.youtube_id +
                        '/0.jpg'
                    "
                  />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from "../../utils/events";
import * as Config from "../../config";
import { mapState, mapGetters } from "vuex";

import moment from "moment";

import YouTubePlayer from "@/components/general/YouTubePlayer";
import StarRating from "@/components/general/StarRating";

export default {
  data() {
    return {
      query: this.$route.query,
      params: this.$route.params,
      videoDetails: null,
      loading: true,
      error: false,
      errorType: false
    };
  },
  mounted() {
    this.getVideoListing();
  },
  components: {
    YouTubePlayer,
    StarRating
  },
  methods: {
    async getVideoListing() {
      try {
        let res = await this.$http.get(
          Config.apiPath +
            "/api/v2/media/preview/v2/" +
            this.$route.params.refId +
            "/" +
            this.$route.params.videoId
        );
        console.log(res.data.message);

        if (res.data.message == "reference_lookup_error") {
          this.error = true;
          this.errorType = "reference_lookup_error";
        }
        this.$set(this, "videoDetails", res.data);
        this.$set(this, "loading", false);
      } catch (e) {
        this.error = true;
        this.errorType = "error";
      }
    },

    async purchaseAndDownload() {
      let result = await this.$swal({
        title: "Continue?",
        text:
          "Downloading this file represents a purchase. If your network downloads this file and does not use it, notify brett@livestormsmedia.com within 24 hours to have the charge voided.",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, purchase and download."
      });

      if (result.isConfirmed) {
        let res = await this.$http.post(
          Config.apiPath +
            "/api/v2/media/preview/v2/" +
            this.$route.params.refId +
            "/" +
            this.$route.params.videoId +
            "/purchase"
        );
        var link = document.createElement("a");
        document.body.appendChild(link);
        link.href = res.data;
        link.click();
        document.body.removeChild(link);

        this.videoDetails.sale = true;
        this.$swal("All good!", "The video download has started.", "success");
      }
    },

    async purchaseUsingCredits() {
      let result = await this.$swal({
        title: "Continue?",
        text:
          "You are about to purchase " +
          this.videoDetails.title +
          " for " +
          this.videoDetails.title +
          " credits.",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, purchase and download."
      });

      if (result.isConfirmed) {
        var data = {
          id: this.videoDetails.id,
          creditAgreementId: this.videoDetails.networkCreditAgreement.id
        };
        let res = await this.$http.post(
          Config.apiPath +
            "/api/v2/media/preview/v2/" +
            this.$route.params.refId +
            "/" +
            this.$route.params.videoId +
            "/creditPurchase",
          data
        );
        var link = document.createElement("a");
        document.body.appendChild(link);
        link.href = res.data;
        link.click();
        document.body.removeChild(link);

        this.videoDetails.sale = true;
        this.$swal("All good!", "The video download has started.", "success");
      }
    },

    redownloadVideo() {
      var _this = this;
      this.$http
        .post(
          Config.apiPath +
            "/api/v2/media/preview/v2/" +
            this.$route.params.refId +
            "/" +
            this.$route.params.videoId +
            "/download",
          {}
        )
        .then(response => {
          var link = document.createElement("a");
          document.body.appendChild(link);
          link.href = response.data;
          link.click();
          document.body.removeChild(link);

          _this.$swal(
            "All good!",
            "The video download has started.",
            "success"
          );
        });
    },
    goToMailing(video) {
      this.$router.push({
        name: "MediaPreviewV2",
        params: { videoId: video.uid, refId: this.$route.params.refId }
      });
    }
  },
  filters: {
    printLocations: function(data) {
      if (data !== null && data !== undefined) {
        var string = "";
        for (var i = 0; i < data.length; i++) {
          string = string + data[i];

          if (i !== data.length - 1) {
            string = string + ", ";
          }
        }
        return string;
      }
    }
  },
  watch: {
    "$route.params": function() {
      this.getVideoListing();
    }
  }
};
</script>

<style>
.aspect-video {
  aspect-ratio: 16 / 9;
}
.stretch {
  padding-bottom: 56.25%; /* 16:9 */
  overflow: hidden;
}
.stretch > img {
  position: absolute;
  top: -16.75%;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
